// [PURPOSE]: Manage in-app review prompt trigger logic and persistence.
// [WHY]: Catches enthusiastic users at their peak moment (first character + repeat use)
//        to ask for a store review. Fires once per install — never nags.
// [INTEGRATION]: Consumed by App.jsx. Pairs with ReviewPromptDialog.jsx.
// [CREATED]: Session 10 — Marketing & Launch

import { useCallback, useEffect, useMemo, useState } from 'react';
import { safeLocalStorageSet } from '../utils/storageValidator';
import { trackEvent } from '../services/analytics';

// [PURPOSE]: Single namespace for all review-prompt localStorage keys
const STORAGE_KEYS = {
  firstSeen: 'tti_review_first_seen_at',
  sessionCount: 'tti_review_session_count',
  prompted: 'tti_review_prompted',
  outcome: 'tti_review_outcome',
};

const SESSION_FLAG = 'tti_session_counted';
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
const MIN_SESSIONS = 3;

/**
 * [PURPOSE]: Read a number from localStorage, safely.
 */
const readNumber = (key, fallback = 0) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
};

/**
 * [PURPOSE]: Build the Play Store review URL for the current app.
 * [WHY]: Google does not expose a deep-link to the review-only sheet from the web;
 *        opening the listing with `showAllReviews=true` scrolls to the reviews section.
 *        When a proper @capacitor-community/in-app-review plugin is installed in a
 *        future release, replace this with the native InAppReview.requestReview() call.
 */
export const getPlayStoreReviewUrl = (appId = 'com.tabletopinventory.app') =>
  `https://play.google.com/store/apps/details?id=${appId}&showAllReviews=true`;

/**
 * [PURPOSE]: Detect whether the app is running inside the Capacitor shell.
 * [WHY]: Native Android gets a Play Store URL; web gets a feedback/support CTA.
 */
export const isNativeAndroid = () => {
  try {
    const cap = typeof window !== 'undefined' ? window.Capacitor : null;
    if (!cap) return false;
    const platform = typeof cap.getPlatform === 'function' ? cap.getPlatform() : cap.platform;
    return platform === 'android';
  } catch {
    return false;
  }
};

/**
 * [PURPOSE]: Hook that decides whether to show the review prompt, and records the outcome.
 * [WHY]: Centralizes the trigger logic so App.jsx only needs a boolean + callbacks.
 *
 * Trigger: hasCharacter AND (sessionCount >= 3 OR firstSeen >= 3 days ago) AND !alreadyPrompted.
 * Fires once. Sets `tti_review_prompted = '1'` after any response. Never shows again.
 *
 * @param {Object} options
 * @param {boolean} options.hasCharacter — true when the user has at least one named character
 * @returns {{
 *   shouldShow: boolean,
 *   recordPositive: () => void,
 *   recordNegative: () => void,
 *   dismiss: () => void,
 *   reset: () => void,
 * }}
 */
const useReviewPrompt = ({ hasCharacter } = { hasCharacter: false }) => {
  // [PURPOSE]: Local mirror of "should this dialog render right now?"
  const [shouldShow, setShouldShow] = useState(false);

  // [PURPOSE]: Bump session counter exactly once per browser/app session on mount.
  // [WHY]: sessionStorage resets per-tab/per-app-launch, localStorage persists forever.
  useEffect(() => {
    try {
      const already = sessionStorage.getItem(SESSION_FLAG);
      if (already) return;

      // First app open ever: stamp firstSeen.
      const firstSeen = readNumber(STORAGE_KEYS.firstSeen, 0);
      if (!firstSeen) {
        safeLocalStorageSet(STORAGE_KEYS.firstSeen, String(Date.now()));
      }

      const next = readNumber(STORAGE_KEYS.sessionCount, 0) + 1;
      safeLocalStorageSet(STORAGE_KEYS.sessionCount, String(next));
      sessionStorage.setItem(SESSION_FLAG, '1');
    } catch {
      // Non-fatal — if storage is blocked, prompt just never fires.
    }
  }, []);

  // [PURPOSE]: Re-evaluate trigger when hasCharacter flips or when app_open fires.
  // [WHY]: Two natural moments to check: first character creation, or later sessions.
  useEffect(() => {
    const evaluate = () => {
      try {
        const prompted = localStorage.getItem(STORAGE_KEYS.prompted);
        if (prompted === '1') {
          setShouldShow(false);
          return;
        }
        if (!hasCharacter) {
          setShouldShow(false);
          return;
        }

        const sessionCount = readNumber(STORAGE_KEYS.sessionCount, 0);
        const firstSeen = readNumber(STORAGE_KEYS.firstSeen, Date.now());
        const ageMs = Date.now() - firstSeen;

        const sessionsMet = sessionCount >= MIN_SESSIONS;
        const ageMet = ageMs >= THREE_DAYS_MS;

        if (sessionsMet || ageMet) {
          setShouldShow(true);
          // [WHY]: Fire analytics at the moment the prompt becomes visible.
          trackEvent('review_prompt_shown', {
            trigger: sessionsMet ? 'sessions' : 'days',
            session_count: sessionCount,
            days_since_first_seen: Math.floor(ageMs / (24 * 60 * 60 * 1000)),
          });
        }
      } catch {
        // Non-fatal
      }
    };

    // Small delay so we don't interrupt initial render / route animation.
    const t = setTimeout(evaluate, 1500);
    return () => clearTimeout(t);
  }, [hasCharacter]);

  // [PURPOSE]: Mark prompt as answered so it never shows again.
  const markPrompted = useCallback((outcome) => {
    try {
      safeLocalStorageSet(STORAGE_KEYS.prompted, '1');
      safeLocalStorageSet(STORAGE_KEYS.outcome, outcome);
    } catch {
      // Non-fatal
    }
    setShouldShow(false);
  }, []);

  const recordPositive = useCallback(() => {
    trackEvent('review_prompt_positive');
    markPrompted('positive');
  }, [markPrompted]);

  const recordNegative = useCallback(() => {
    trackEvent('review_prompt_negative');
    markPrompted('negative');
  }, [markPrompted]);

  const dismiss = useCallback(() => {
    trackEvent('review_prompt_dismissed');
    markPrompted('dismissed');
  }, [markPrompted]);

  // [PURPOSE]: Dev-only helper — clears prompted flag so the dialog can re-fire.
  // [WHY]: Used from Developer/Debug settings section and from unit tests.
  const reset = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.prompted);
      localStorage.removeItem(STORAGE_KEYS.outcome);
      sessionStorage.removeItem(SESSION_FLAG);
    } catch {
      // Non-fatal
    }
  }, []);

  return useMemo(
    () => ({ shouldShow, recordPositive, recordNegative, dismiss, reset }),
    [shouldShow, recordPositive, recordNegative, dismiss, reset],
  );
};

export default useReviewPrompt;
export { STORAGE_KEYS, MIN_SESSIONS, THREE_DAYS_MS };
